import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { authGet } from '../../utils/auth';
import { ArrowLeft, RefreshCw, CheckCircle2, Save, Trash2, AlertTriangle, Globe, ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';


const API = '/api';

// All available fields from BTCMap data
// systemGenerated: true = fields derived/created by our system during sync (shown in orange)
// systemGenerated: false = fields directly imported from BTCMap/OSM
// osmTag: the actual OSM tag name for BTCMap-sourced fields
const AVAILABLE_FIELDS = [
  { key: 'business_name', label: 'Business Name', default: true, systemGenerated: false, osmTag: 'name' },
  { key: 'category', label: 'Category', default: true, systemGenerated: true },
  { key: 'region', label: 'Region', default: true, systemGenerated: true },
  { key: 'based_state', label: 'Based State', default: false, systemGenerated: true },
  { key: 'country_code', label: 'Country', default: true, systemGenerated: true },
  { key: 'payment_lightning', label: 'Lightning', default: true, systemGenerated: false, osmTag: 'payment:lightning' },
  { key: 'payment_onchain', label: 'Onchain', default: false, systemGenerated: false, osmTag: 'payment:onchain' },
  { key: 'opening_hours', label: 'Opening Hours', default: true, systemGenerated: false, osmTag: 'opening_hours' },
  { key: 'full_address', label: 'Full Address', default: true, systemGenerated: true },  // Aggregated from addr: tags
  { key: 'addr_housenumber', label: 'House Number', default: false, systemGenerated: false, osmTag: 'addr:housenumber' },
  { key: 'addr_street', label: 'Street', default: false, systemGenerated: false, osmTag: 'addr:street' },
  { key: 'addr_suburb', label: 'Suburb', default: false, systemGenerated: false, osmTag: 'addr:suburb' },
  { key: 'addr_city', label: 'City', default: false, systemGenerated: false, osmTag: 'addr:city' },
  { key: 'addr_state', label: 'State (addr)', default: false, systemGenerated: false, osmTag: 'addr:state' },
  { key: 'addr_postcode', label: 'Postcode', default: false, systemGenerated: false, osmTag: 'addr:postcode' },
  { key: 'addr_country', label: 'Country (addr)', default: false, systemGenerated: false, osmTag: 'addr:country' },
  { key: 'contact', label: 'Contact', default: true, systemGenerated: false, osmTag: 'phone / contact:phone' },
  { key: 'website', label: 'Website', default: true, systemGenerated: false, osmTag: 'website' },
  { key: 'osm_url', label: 'OSM Link', default: false, systemGenerated: true },
  { key: 'amenity_type', label: 'Amenity Type', default: false, systemGenerated: false, osmTag: 'amenity / shop / tourism' },
  { key: 'latitude', label: 'Latitude', default: false, systemGenerated: false, osmTag: 'lat (coordinates)' },
  { key: 'longitude', label: 'Longitude', default: false, systemGenerated: false, osmTag: 'lon (coordinates)' },
  { key: 'btc_map_id', label: 'BTCMap ID', default: false, systemGenerated: false, osmTag: 'element.id' },
  { key: 'btcmap_created_at', label: 'BTCMap Created', default: false, systemGenerated: false, osmTag: 'created_at' },
  { key: 'btcmap_verified_at', label: 'BTCMap Verified', default: false, systemGenerated: false, osmTag: 'verified_at' },
  { key: 'notes', label: 'Notes', default: false, systemGenerated: true },
  { key: 'location_tier', label: 'Location Tier', default: false, systemGenerated: true },
  { key: 'created_at', label: 'Synced At', default: false, systemGenerated: true },
  { key: 'updated_at', label: 'Updated At', default: false, systemGenerated: true },
];

// Feature status badge component
const FeatureStatus = ({ implemented }) => (
  <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
    implemented 
      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' 
      : 'bg-muted text-muted-foreground'
  }`}>
    <CheckCircle2 className="w-3 h-3" />
    {implemented ? 'Implemented' : 'Not Implemented'}
  </span>
);

const MapSettings = () => {
  const navigate = useNavigate();
  const [syncing, setSyncing] = useState(false);
  const [purging, setPurging] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncStats, setSyncStats] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [btcmapBusinesses, setBtcmapBusinesses] = useState([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [allCountries, setAllCountries] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  // Country-selection dirtiness is tracked separately from settings dirtiness so
  // saving the country dialog never discards unsaved map-display edits.
  const [countryDirty, setCountryDirty] = useState(false);
  // Snapshot of the selection when the dialog opens, so Cancel can revert.
  const [countrySnapshot, setCountrySnapshot] = useState([]);
  const [countryCounts, setCountryCounts] = useState({});
  const [countryRegions, setCountryRegions] = useState({});
  const [loadingCounts, setLoadingCounts] = useState(false);
  const [countryDialog, setCountryDialog] = useState(false);
  const [collapsedRegions, setCollapsedRegions] = useState({});
  const [dataFieldsModal, setDataFieldsModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [visibleFields, setVisibleFields] = useState(
    AVAILABLE_FIELDS.filter(f => f.default).map(f => f.key)
  );
  const [totalBtcmapCount, setTotalBtcmapCount] = useState(0);
  const itemsPerPage = 50;
  
  const [settings, setSettings] = useState({
    enableBTCMapSync: false,
    btcmapFilterCountry: 'AU',
    btcmapAutoSync: false,
    btcmapSyncInterval: 24,
    // Extended data display settings
    showOpeningHours: true,
    showLightningBadge: true,
    showOsmLink: false,
    showFullAddress: true,
    showContact: true
  });
  
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await authGet('/auth/me');
        if (!response.data || response.data.role !== 'admin') {
          navigate('/admin/login');
          return;
        }
        loadSettings();
        loadBtcmapBusinesses();
        loadCountries();
        loadCountryCounts();
      } catch (error) {
        navigate('/admin/login');
      }
    };
    checkAuth();
  }, [navigate]);

  // Refetch the current server page whenever it changes (skip first mount —
  // page 1 is already loaded by checkAuth).
  const didMountBiz = useRef(false);
  useEffect(() => {
    if (!didMountBiz.current) { didMountBiz.current = true; return; }
    loadBtcmapBusinesses(currentPage);
  }, [currentPage]);

  // Warn before leaving the page with unsaved changes (settings, country
  // selection, or visible-field choices).
  useEffect(() => {
    if (!hasUnsavedChanges && !countryDirty) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges, countryDirty]);

  const loadCountries = async () => {
    try {
      const response = await axios.get(`${API}/admin/general-settings`);
      const enabled = response.data?.enabled_countries;
      if (Array.isArray(enabled) && enabled.length > 0) {
        setSelectedCountries(enabled.map(c => (c || '').toUpperCase()));
      }
      // Full country list + names/regions populated from /btcmap/country-counts.
    } catch (error) {
      console.error('Error loading countries:', error);
    }
  };

  const loadCountryCounts = async () => {
    setLoadingCounts(true);
    try {
      const response = await axios.get(`${API}/admin/btcmap/country-counts`);
      if (Array.isArray(response.data)) {
        const countsMap = {};
        const regionsMap = {};
        response.data.forEach(c => {
          countsMap[c.country_code] = c.place_count;
          if (c.region) regionsMap[c.country_code] = c.region;
        });
        setCountryCounts(countsMap);
        setCountryRegions(regionsMap);
        // Always update allCountries with the full list from count data
        setAllCountries(response.data.map(c => ({
          code: c.country_code,
          name: c.name,
          region: c.region,
          enabled: c.enabled,
        })));
        // Preserve existing selections; otherwise seed from the backend's
        // `enabled` flags — but ONLY when they reflect an actual narrowed
        // filter. When no explicit filter is stored the backend marks every
        // country enabled; materialising that full set into selectedCountries
        // would later persist a finite list (excluding OL-native-only
        // countries). Keep "all enabled" implicit (empty) instead — matching
        // the backend (empty enabled_countries = all enabled).
        setSelectedCountries(prev => {
          if (prev.length > 0) return prev;
          const enabledCodes = response.data.filter(c => c.enabled).map(c => c.country_code);
          if (enabledCodes.length === response.data.length) return prev; // all enabled = implicit all
          return enabledCodes;
        });
      }
    } catch (error) {
      console.error('Error loading country counts:', error);
    } finally {
      setLoadingCounts(false);
    }
  };
  
  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/map-settings`);
      if (response.data) {
        const { visibleFields: savedFields, ...rest } = response.data;
        setSettings(prev => ({ ...prev, ...rest }));
        if (Array.isArray(savedFields) && savedFields.length > 0) {
          setVisibleFields(savedFields);
        }
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  // Fetch a single server page of BTCMap businesses using the server-side
  // filter + pagination (paginated envelope: {items, total, next_offset}).
  // Avoids pulling the entire /businesses collection into the browser.
  const loadBtcmapBusinesses = async (page = 1) => {
    setLoadingFields(true);
    try {
      const offset = (page - 1) * itemsPerPage;
      const response = await axios.get(`${API}/businesses`, {
        params: { btc_map: true, paginated: true, limit: itemsPerPage, offset },
      });
      const data = response.data || {};
      setBtcmapBusinesses(Array.isArray(data.items) ? data.items : []);
      setTotalBtcmapCount(typeof data.total === 'number' ? data.total : 0);
    } catch (error) {
      console.error('Error loading BTCMap businesses:', error);
    } finally {
      setLoadingFields(false);
    }
  };
  
  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasUnsavedChanges(true);
  };

  // Mutating the country selection is an unsaved change too — but tracked
  // separately so the country dialog's save doesn't clear settings dirtiness.
  const updateSelectedCountries = (updater) => {
    setSelectedCountries(updater);
    setCountryDirty(true);
  };

  // Resolve the effective sync scope. An EMPTY selection means the backend
  // treats it as "all countries" (worldwide) — surface that explicitly so an
  // admin never triggers a worldwide pull/purge by accident.
  const resolveSyncScope = () => {
    if (selectedCountries.length === 0) {
      return { worldwide: true, label: 'ALL countries worldwide' };
    }
    const names = selectedCountries.map(c => allCountries.find(x => x.code === c)?.name || c);
    return {
      worldwide: false,
      label: `${selectedCountries.length} countr${selectedCountries.length === 1 ? 'y' : 'ies'} (${names.join(', ')})`,
    };
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/map-settings`, { ...settings, visibleFields });
      setHasUnsavedChanges(false);
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };
  
  const handlePurgeAndSync = async () => {
    const scope = resolveSyncScope();
    if (scope.worldwide) {
      // No country selected resolves to a WORLDWIDE purge + re-import (tens of
      // thousands of places). Never let this happen on a stray click — require
      // an explicit typed confirmation.
      const typed = window.prompt(
        'No countries are selected, so this would PURGE the entire BTCMap dataset and re-import EVERY country worldwide (tens of thousands of places).\n\n' +
        'This is almost never what you want — Cancel and select specific countries, or type "worldwide" to proceed anyway.'
      );
      if ((typed || '').trim().toLowerCase() !== 'worldwide') {
        toast.error('Fresh sync cancelled — select countries first, or type "worldwide" to confirm.');
        return;
      }
    } else if (!window.confirm(`This will DELETE all existing BTCMap data and re-import ${scope.label}. This may take a few minutes. Continue?`)) {
      return;
    }

    setSyncing(true);
    try {
      // First save settings
      await axios.post(`${API}/admin/map-settings`, settings);
      setHasUnsavedChanges(false);

      // Then sync with purge flag. In the worldwide case send an EMPTY scope
      // (no 'AU' fallback) so the backend re-imports every country after the
      // purge — otherwise we'd wipe all data and re-import AU only.
      const syncConfig = scope.worldwide
        ? {
            filterCountry: '',
            filterCountries: [],
            updateExisting: true,
            createNew: true,
            purgeFirst: true
          }
        : {
            filterCountry: selectedCountries[0] || settings.btcmapFilterCountry || 'AU',
            filterCountries: selectedCountries,
            updateExisting: true,
            createNew: true,
            purgeFirst: true
          };

      const response = await axios.post(`${API}/admin/btcmap/sync`, syncConfig, { timeout: 180000 });
      setSyncStats(response.data);
      toast.success(`Fresh sync complete! Imported ${response.data.synced_count} businesses from BTCMap.org`);
      // Reload BTCMap businesses to show updated data
      setCurrentPage(1);
      loadBtcmapBusinesses(1);
    } catch (error) {
      console.error('Error syncing BTCMap:', error);
      toast.error('Failed to sync BTCMap data. The request may have timed out - try again.');
    } finally {
      setSyncing(false);
    }
  };
  
  const handlePurgeOnly = async () => {
    if (!window.confirm('This will DELETE all BTCMap imported businesses. OrangeLabel businesses will NOT be affected. Continue?')) {
      return;
    }
    
    setPurging(true);
    try {
      const response = await axios.post(`${API}/admin/btcmap/purge`);
      toast.success(`Deleted ${response.data.deleted_count} BTCMap businesses`);
      setSyncStats(null);
    } catch (error) {
      console.error('Error purging BTCMap:', error);
      toast.error('Failed to purge BTCMap data');
    } finally {
      setPurging(false);
    }
  };

  const handleSaveAndSync = async () => {
    const scope = resolveSyncScope();
    if (scope.worldwide && !window.confirm(
      'No countries are selected, so this will sync EVERY country worldwide (tens of thousands of places). ' +
      'Select specific countries to narrow the import, or continue worldwide?'
    )) {
      return;
    }

    setSyncing(true);
    try {
      // First save settings
      await axios.post(`${API}/admin/map-settings`, settings);
      setHasUnsavedChanges(false);

      // Then sync (update existing only). In the worldwide case send an EMPTY
      // scope (no 'AU' fallback) so the backend covers every country.
      const syncConfig = scope.worldwide
        ? {
            filterCountry: '',
            filterCountries: [],
            updateExisting: true,
            createNew: true,
            purgeFirst: false
          }
        : {
            filterCountry: selectedCountries[0] || settings.btcmapFilterCountry || 'AU',
            filterCountries: selectedCountries,
            updateExisting: true,
            createNew: true,
            purgeFirst: false
          };

      const response = await axios.post(`${API}/admin/btcmap/sync`, syncConfig, { timeout: 180000 });
      setSyncStats(response.data);
      toast.success(`Synced ${response.data.synced_count} new, updated ${response.data.updated_count} existing businesses`);
      // Reload BTCMap businesses to show updated data
      setCurrentPage(1);
      loadBtcmapBusinesses(1);
    } catch (error) {
      console.error('Error syncing BTCMap:', error);
      toast.error('Failed to sync BTCMap data');
    } finally {
      setSyncing(false);
    }
  };

  // Group countries by region, regions sorted by aggregate count desc
  const groupedRegions = useMemo(() => {
    const regionMap = {};
    allCountries.forEach(c => {
      const region = c.region || countryRegions[c.code] || 'Other';
      if (!regionMap[region]) regionMap[region] = [];
      regionMap[region].push(c);
    });
    // Sort countries within each region by place count desc
    Object.values(regionMap).forEach(arr => {
      arr.sort((a, b) => (countryCounts[b.code] || 0) - (countryCounts[a.code] || 0));
    });
    // Sort regions by aggregate count desc
    return Object.entries(regionMap).sort((a, b) => {
      const sumA = a[1].reduce((s, c) => s + (countryCounts[c.code] || 0), 0);
      const sumB = b[1].reduce((s, c) => s + (countryCounts[c.code] || 0), 0);
      return sumB - sumA;
    });
  }, [allCountries, countryCounts, countryRegions]);

  const toggleRegion = (region) => {
    setCollapsedRegions(prev => ({ ...prev, [region]: !prev[region] }));
  };

  const expandAll = () => setCollapsedRegions({});
  const collapseAll = () => {
    const all = {};
    groupedRegions.forEach(([region]) => { all[region] = true; });
    setCollapsedRegions(all);
  };

  // Only countries with at least 1 BTCMap place are selectable via Select All
  const selectableCountryCodes = useMemo(
    () => allCountries.filter(c => (countryCounts[c.code] || 0) > 0).map(c => c.code),
    [allCountries, countryCounts]
  );
  const allSelected = selectableCountryCodes.length > 0 &&
    selectableCountryCodes.every(code => selectedCountries.includes(code));
  const toggleSelectAll = () => {
    if (allSelected) {
      updateSelectedCountries(prev => prev.filter(c => !selectableCountryCodes.includes(c)));
    } else {
      updateSelectedCountries(prev => [...new Set([...prev, ...selectableCountryCodes])]);
    }
  };

  // Helper to render field values in the table
  const renderFieldValue = (business, fieldKey) => {
    const value = business[fieldKey];
    
    switch (fieldKey) {
      case 'category':
        return Array.isArray(value) ? value.join(', ') : (value || '-');
      case 'payment_lightning':
      case 'payment_onchain':
        return value ? '⚡ Yes' : '-';
      case 'latitude':
      case 'longitude':
        return value ? value.toFixed(6) : '-';
      case 'website':
        return value ? (
          <a href={value.startsWith('http') ? value : `https://${value}`} target="_blank" rel="noopener noreferrer" 
             className="text-primary hover:underline truncate block max-w-[180px]" title={value}>
            {value.replace(/^https?:\/\//, '').replace(/\/$/, '')}
          </a>
        ) : '-';
      case 'osm_url':
        return value ? (
          <a href={value} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">OSM</a>
        ) : '-';
      case 'created_at':
      case 'updated_at':
      case 'btcmap_created_at':
      case 'btcmap_verified_at':
        if (!value) return '-';
        try {
          return new Date(value).toLocaleDateString();
        } catch {
          return value;
        }
      case 'notes':
      case 'full_address':
      case 'opening_hours':
        return value ? (
          <span className="truncate block max-w-[180px]" title={value}>{value}</span>
        ) : '-';
      default:
        return value || '-';
    }
  };

  return (
    <div className="min-h-screen bg-muted">
      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin')}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-6 h-6" strokeWidth={1.5} />
            </button>
            <h1 className="text-2xl font-heading font-bold text-foreground">
              BTCMap Management
            </h1>
          </div>
        </div>
      </div>
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Feature Status Overview */}
        <div className="bg-card border border-border rounded-xl p-6 mb-6">
          <h2 className="text-xl font-heading font-semibold mb-4">Feature Status</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Overview of BTCMap integration and business management features.
          </p>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">Feature</th>
                  <th className="px-4 py-2 text-center font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                <tr><td className="px-4 py-3">Country Filtering (AU, NZ)</td><td className="px-4 py-3 text-center"><FeatureStatus implemented={true} /></td></tr>
                <tr><td className="px-4 py-3">Lightning Payment Detection</td><td className="px-4 py-3 text-center"><FeatureStatus implemented={true} /></td></tr>
                <tr><td className="px-4 py-3">Opening Hours</td><td className="px-4 py-3 text-center"><FeatureStatus implemented={true} /></td></tr>
                <tr><td className="px-4 py-3">OSM Link</td><td className="px-4 py-3 text-center"><FeatureStatus implemented={true} /></td></tr>
                <tr><td className="px-4 py-3">Data Purge & Fresh Sync</td><td className="px-4 py-3 text-center"><FeatureStatus implemented={true} /></td></tr>
                <tr><td className="px-4 py-3">Auto-Sync Scheduling</td><td className="px-4 py-3 text-center"><FeatureStatus implemented={true} /></td></tr>
                <tr className="bg-muted/30"><td className="px-4 py-3 text-muted-foreground">Create New Business UI (Website)</td><td className="px-4 py-3 text-center"><FeatureStatus implemented={false} /></td></tr>
                <tr className="bg-muted/30"><td className="px-4 py-3 text-muted-foreground">Update Business UI (Website)</td><td className="px-4 py-3 text-center"><FeatureStatus implemented={false} /></td></tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* BTCMap Sync Section */}
        <div className="bg-card border border-border rounded-xl p-8 mb-6">
          <h2 className="text-xl font-heading font-semibold mb-4">BTCMap.org Synchronization</h2>
          <p className="text-base text-muted-foreground mb-6">
            Sync business data from BTCMap.org. The sync uses coordinate-based filtering to ensure only businesses 
            within the selected country are imported.
          </p>
          
          <div className="flex items-center justify-between mb-6 pb-6 border-b border-border">
            <div>
              <Label htmlFor="enable-sync" className="text-base font-medium">Enable BTCMap Integration</Label>
              <p className="text-sm text-muted-foreground mt-1">Allow importing businesses from BTCMap.org</p>
            </div>
            <Switch
              id="enable-sync"
              checked={settings.enableBTCMapSync}
              onCheckedChange={(value) => handleSettingChange('enableBTCMapSync', value)}
            />
          </div>
          
          {settings.enableBTCMapSync && (
            <>
              {/* Filtering */}
              <div className="space-y-4 mb-6 pb-6 border-b border-border">
                <h3 className="text-base font-heading font-semibold">Geographic Filtering</h3>
                <p className="text-xs text-muted-foreground">
                  Countries selected here drive both the BTCMap sync and the set
                  of countries enabled for the public directory — this list is
                  the same as General Settings › Countries.
                </p>
                {loadingCounts ? (
                  <div className="animate-pulse space-y-2">
                    <div className="h-10 w-64 bg-muted rounded" />
                    <div className="h-6 w-40 bg-muted rounded" />
                  </div>
                ) : (
                  <div className="flex items-center gap-4 flex-wrap">
                    <Button
                      onClick={() => { setCountrySnapshot(selectedCountries); setCountryDialog(true); }}
                      variant="outline"
                      className="inline-flex items-center gap-2"
                    >
                      <Globe className="w-4 h-4" />
                      Select Countries ({selectedCountries.length === 0 ? 'all' : selectedCountries.length} selected)
                    </Button>

                    <div className="flex flex-wrap gap-2">
                      {selectedCountries.length === 0 ? (
                        <span className="text-xs text-amber-600">
                          No filter set — <strong>all countries (worldwide)</strong> are enabled, matching General Settings. Sync/purge will affect every country until you select a subset.
                        </span>
                      ) : selectedCountries.map(code => {
                        const country = allCountries.find(c => c.code === code);
                        return (
                          <span key={code} className="px-2 py-1 bg-primary/10 text-primary text-xs rounded">
                            {country?.name || code}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Auto-Sync */}
              <div className="space-y-4 mb-6 pb-6 border-b border-border">
                <h3 className="text-base font-heading font-semibold">Auto-Sync Schedule</h3>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="auto-sync" className="text-sm font-medium">Enable Auto-Sync</Label>
                    <p className="text-xs text-muted-foreground mt-1">Automatically sync at regular intervals</p>
                  </div>
                  <Switch
                    id="auto-sync"
                    checked={settings.btcmapAutoSync}
                    onCheckedChange={(value) => handleSettingChange('btcmapAutoSync', value)}
                  />
                </div>
                
                {settings.btcmapAutoSync && (
                  <div className="space-y-2 ml-6">
                    <Label htmlFor="sync-interval">Sync Interval (hours)</Label>
                    <Input
                      id="sync-interval"
                      type="number"
                      min="1"
                      max="168"
                      value={settings.btcmapSyncInterval}
                      onChange={(e) => handleSettingChange('btcmapSyncInterval', parseInt(e.target.value) || 24)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Recommended: 168 hours (weekly) for fresh data sync
                    </p>
                  </div>
                )}
              </div>
              
              {/* Extended Data Display */}
              <div className="space-y-4 mb-6 pb-6 border-b border-border">
                <h3 className="text-base font-heading font-semibold">Data Display Settings</h3>
                <p className="text-xs text-muted-foreground mb-3">Overview of which fields are displayed on each view</p>
                
                <div className="overflow-x-auto border border-border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-muted">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium">Field</th>
                        <th className="px-4 py-2 text-center font-medium">Map List Cards</th>
                        <th className="px-4 py-2 text-center font-medium">Map Popup</th>
                        <th className="px-4 py-2 text-center font-medium">Directory</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      <tr>
                        <td className="px-4 py-2">Business Name</td>
                        <td className="px-4 py-2 text-center text-green-600">✓</td>
                        <td className="px-4 py-2 text-center text-green-600">✓</td>
                        <td className="px-4 py-2 text-center text-green-600">✓</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2">Source Logo (OrangeLabel/BTCMap)</td>
                        <td className="px-4 py-2 text-center text-green-600">✓</td>
                        <td className="px-4 py-2 text-center text-green-600">✓</td>
                        <td className="px-4 py-2 text-center text-green-600">✓</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2">ABIB Logo</td>
                        <td className="px-4 py-2 text-center text-green-600">✓</td>
                        <td className="px-4 py-2 text-center text-green-600">✓</td>
                        <td className="px-4 py-2 text-center text-green-600">✓</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2">Lightning Logo</td>
                        <td className="px-4 py-2 text-center text-green-600">✓</td>
                        <td className="px-4 py-2 text-center text-green-600">✓</td>
                        <td className="px-4 py-2 text-center text-green-600">✓</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2">Location/Region</td>
                        <td className="px-4 py-2 text-center text-green-600">✓</td>
                        <td className="px-4 py-2 text-center text-green-600">✓</td>
                        <td className="px-4 py-2 text-center text-green-600">✓</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2">Opening Hours</td>
                        <td className="px-4 py-2 text-center text-green-600">✓</td>
                        <td className="px-4 py-2 text-center text-green-600">✓</td>
                        <td className="px-4 py-2 text-center text-green-600">✓</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2">Website</td>
                        <td className="px-4 py-2 text-center text-green-600">✓</td>
                        <td className="px-4 py-2 text-center text-green-600">✓</td>
                        <td className="px-4 py-2 text-center text-green-600">✓</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2">Contact</td>
                        <td className="px-4 py-2 text-center text-green-600">✓</td>
                        <td className="px-4 py-2 text-center text-green-600">✓</td>
                        <td className="px-4 py-2 text-center text-green-600">✓</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2">Categories</td>
                        <td className="px-4 py-2 text-center text-muted-foreground">—</td>
                        <td className="px-4 py-2 text-center text-green-600">✓</td>
                        <td className="px-4 py-2 text-center text-green-600">✓</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2">Full Address</td>
                        <td className="px-4 py-2 text-center text-muted-foreground">—</td>
                        <td className="px-4 py-2 text-center text-green-600">✓</td>
                        <td className="px-4 py-2 text-center text-muted-foreground">—</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2">Notes</td>
                        <td className="px-4 py-2 text-center text-muted-foreground">—</td>
                        <td className="px-4 py-2 text-center text-green-600">✓</td>
                        <td className="px-4 py-2 text-center text-muted-foreground">—</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
          
          {/* Action Buttons */}
          <div className="space-y-4">
            <div className="flex gap-3">
              <Button
                onClick={saveSettings}
                disabled={!hasUnsavedChanges || saving}
                variant="outline"
                className="inline-flex items-center gap-2"
              >
                <Save className={`w-4 h-4 ${saving ? 'animate-pulse' : ''}`} strokeWidth={1.5} />
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
              
              <Button
                onClick={handleSaveAndSync}
                disabled={!settings.enableBTCMapSync || syncing}
                className="inline-flex items-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} strokeWidth={1.5} />
                {syncing ? 'Syncing...' : 'Save and Sync'}
              </Button>
            </div>
            
            {settings.enableBTCMapSync && (
              <div className="flex gap-3 pt-2 border-t border-border">
                <Button
                  onClick={handlePurgeAndSync}
                  disabled={syncing}
                  variant="outline"
                  className="inline-flex items-center gap-2 border-orange-300 text-orange-600 hover:bg-orange-50"
                >
                  <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} strokeWidth={1.5} />
                  Fresh Sync (Purge & Re-import)
                </Button>
                
                <Button
                  onClick={handlePurgeOnly}
                  disabled={purging}
                  variant="outline"
                  className="inline-flex items-center gap-2 border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  <Trash2 className={`w-4 h-4 ${purging ? 'animate-pulse' : ''}`} strokeWidth={1.5} />
                  {purging ? 'Purging...' : 'Purge All BTCMap Data'}
                </Button>
              </div>
            )}
          </div>
          
          {hasUnsavedChanges && (
            <p className="mt-2 text-sm text-yellow-600">You have unsaved changes</p>
          )}
          
          {syncStats && (
            <div className="mt-4 p-4 bg-muted rounded-md">
              <p className="text-sm font-medium mb-2">Last Sync Results:</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">New</p>
                  <p className="text-lg font-semibold text-green-600">{syncStats.synced_count}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Updated</p>
                  <p className="text-lg font-semibold text-primary">{syncStats.updated_count}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Skipped</p>
                  <p className="text-lg font-semibold text-muted-foreground">{syncStats.skipped_count}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Total Processed</p>
                  <p className="text-lg font-semibold">{syncStats.total_elements}</p>
                </div>
              </div>
            </div>
          )}
          
          <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5" />
              <div className="text-xs text-yellow-700">
                <p className="font-medium">Recommended: Weekly Fresh Sync</p>
                <p className="mt-1">Use &quot;Fresh Sync&quot; weekly to ensure data is up-to-date and remove businesses that have been deleted from BTCMap.org.</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* BTCMap Data Fields Section - Button to Open Modal */}
        <div className="bg-card border border-border rounded-xl p-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-heading font-semibold">BTCMap Data Fields</h2>
              <p className="text-sm text-muted-foreground">
                {totalBtcmapCount} businesses synced. View and explore all imported data.
              </p>
            </div>
            <Button onClick={() => setDataFieldsModal(true)}>
              View Data Fields
            </Button>
          </div>
        </div>
        
        {/* BTCMap Data Fields Modal */}
        <Dialog open={dataFieldsModal} onOpenChange={setDataFieldsModal}>
          <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>BTCMap Data Fields</DialogTitle>
              <p className="text-sm text-muted-foreground">
                {totalBtcmapCount} businesses synced. Showing page {currentPage} of {Math.ceil(totalBtcmapCount / itemsPerPage) || 1}
              </p>
            </DialogHeader>
            
            {/* Column Selector */}
            <div className="border border-border rounded-lg p-3 bg-muted/30">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-muted-foreground font-medium">Select columns ({visibleFields.length}/{AVAILABLE_FIELDS.length}):</p>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span><span className="inline-block w-2 h-2 rounded-full bg-orange-400 mr-1"></span>System generated</span>
                  <span><span className="inline-block w-2 h-2 rounded-full bg-blue-400 mr-1"></span>BTCMap sourced</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                {AVAILABLE_FIELDS.map(field => (
                  <label 
                    key={field.key} 
                    className={`flex items-center gap-1.5 text-xs cursor-pointer hover:bg-accent px-1.5 py-0.5 rounded ${
                      field.systemGenerated ? 'text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/20' : 'text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20'
                    }`}
                  >
                    <Checkbox
                      checked={visibleFields.includes(field.key)}
                      onCheckedChange={(checked) => {
                        setVisibleFields(prev => checked
                          ? [...prev, field.key]
                          : prev.filter(f => f !== field.key));
                        setHasUnsavedChanges(true);
                      }}
                    />
                    {field.label}
                  </label>
                ))}
              </div>
            </div>
            
            {/* Data Table */}
            <div className="flex-1 overflow-auto">
              {loadingFields ? (
                <p className="text-muted-foreground p-4">Loading BTCMap data...</p>
              ) : btcmapBusinesses.length === 0 ? (
                <p className="text-muted-foreground p-4">No BTCMap businesses synced yet.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-muted sticky top-0">
                    <tr>
                      {AVAILABLE_FIELDS.filter(f => visibleFields.includes(f.key)).map(field => (
                        <th 
                          key={field.key} 
                          className={`px-3 py-2 text-left font-medium border-b whitespace-nowrap ${
                            field.systemGenerated ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-800 dark:text-orange-300' : 'bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300'
                          }`}
                        >
                          <div>{field.label}</div>
                          {field.osmTag && (
                            <div className="text-[10px] font-normal opacity-70">{field.osmTag}</div>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {btcmapBusinesses
                      .map((b) => (
                      <tr key={b.id} className="hover:bg-muted/50">
                        {AVAILABLE_FIELDS.filter(f => visibleFields.includes(f.key)).map(field => (
                          <td 
                            key={field.key} 
                            className={`px-3 py-2 max-w-[200px] ${field.systemGenerated ? 'bg-orange-50/30' : ''}`}
                          >
                            {renderFieldValue(b, field.key)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            
            {/* Pagination */}
            {btcmapBusinesses.length > 0 && (
              <div className="flex items-center justify-between pt-4 border-t border-border">
                <p className="text-sm text-muted-foreground">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalBtcmapCount)} of {totalBtcmapCount}
                </p>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>First</Button>
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm px-3">Page {currentPage}</span>
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(Math.ceil(totalBtcmapCount / itemsPerPage), p + 1))} disabled={currentPage >= Math.ceil(totalBtcmapCount / itemsPerPage)}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(Math.ceil(totalBtcmapCount / itemsPerPage))} disabled={currentPage >= Math.ceil(totalBtcmapCount / itemsPerPage)}>Last</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
        
        {/* Country Selection Dialog */}
        <Dialog open={countryDialog} onOpenChange={(open) => {
          if (open) {
            setCountrySnapshot(selectedCountries);
            setCountryDialog(true);
          } else {
            // Dismissed via overlay/esc = cancel: revert to the snapshot.
            setSelectedCountries(countrySnapshot);
            setCountryDirty(false);
            setCountryDialog(false);
          }
        }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Select Countries for BTCMap Sync</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 pt-2">
              <p className="text-sm text-muted-foreground">
                Select which countries to include when syncing from BTCMap.org.
                Leaving every country unselected means <strong>all countries (worldwide)</strong> — the same as General Settings.
                {loadingCounts && <span className="ml-2 italic">(Loading counts…)</span>}
              </p>

              {/* Controls row */}
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  {selectedCountries.length === 0
                    ? 'All countries (worldwide)'
                    : `${selectedCountries.length} countr${selectedCountries.length === 1 ? 'y' : 'ies'} selected`}
                </span>
                <div className="flex gap-2">
                  <button onClick={toggleSelectAll} className="hover:text-foreground underline font-medium">
                    {allSelected ? 'Deselect All' : 'Select All'}
                  </button>
                  <span className="text-border">|</span>
                  <button onClick={expandAll} className="hover:text-foreground underline">Expand all</button>
                  <button onClick={collapseAll} className="hover:text-foreground underline">Collapse all</button>
                </div>
              </div>

              {/* Grouped regions */}
              <div className="border border-border rounded-lg max-h-[60vh] overflow-y-auto divide-y divide-border">
                {groupedRegions.map(([region, countries]) => {
                  const regionTotal = countries.reduce((s, c) => s + (countryCounts[c.code] || 0), 0);
                  const regionSelected = countries.filter(c => selectedCountries.includes(c.code)).length;
                  const isCollapsed = collapsedRegions[region];
                  return (
                    <div key={region}>
                      {/* Region header */}
                      <button
                        onClick={() => toggleRegion(region)}
                        className="w-full flex items-center justify-between px-3 py-2 bg-muted/50 hover:bg-muted text-sm font-medium"
                      >
                        <span className="flex items-center gap-2">
                          {isCollapsed ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
                          {region}
                          {regionSelected > 0 && (
                            <span className="px-1.5 py-0.5 bg-primary/10 text-primary text-xs rounded">
                              {regionSelected} selected
                            </span>
                          )}
                        </span>
                        <span className="text-xs text-muted-foreground tabular-nums">
                          {regionTotal.toLocaleString()} places
                        </span>
                      </button>
                      {/* Countries list */}
                      {!isCollapsed && (
                        <div className="divide-y divide-border/50">
                          {countries.map(country => {
                            const count = countryCounts[country.code] || 0;
                            return (
                              <div key={country.code} className="flex items-center px-4 py-1.5 hover:bg-muted/30">
                                <Checkbox
                                  id={`country-${country.code}`}
                                  checked={selectedCountries.includes(country.code)}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      updateSelectedCountries(prev => [...prev, country.code]);
                                    } else {
                                      updateSelectedCountries(prev => prev.filter(c => c !== country.code));
                                    }
                                  }}
                                  className="mr-2"
                                />
                                <label htmlFor={`country-${country.code}`} className="text-sm cursor-pointer flex items-center gap-2 flex-1 min-w-0">
                                  <span className="font-mono text-xs bg-muted px-1 rounded shrink-0">{country.code}</span>
                                  <span className="truncate">{country.name}</span>
                                  <span className="ml-auto text-xs text-muted-foreground tabular-nums shrink-0">
                                    {count.toLocaleString()}
                                  </span>
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
                {groupedRegions.length === 0 && !loadingCounts && (
                  <div className="p-4 border border-amber-200 bg-amber-50 dark:bg-amber-900/20 rounded text-sm">
                    <p className="font-medium text-amber-800 dark:text-amber-200 mb-1">No country data available</p>
                    <p className="text-amber-700 dark:text-amber-300 text-xs">
                      Could not fetch country coverage from BTCMap.org.
                      Check network connectivity and refresh, or save any
                      General Settings change to re-run the aggregation.
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => {
                  // Cancel: revert checkbox edits to the snapshot taken on open.
                  setSelectedCountries(countrySnapshot);
                  setCountryDirty(false);
                  setCountryDialog(false);
                }}>Cancel</Button>
                <Button onClick={async () => {
                  try {
                    // Only persist an explicit list when the admin has actually
                    // narrowed the selection. "All enabled" (empty or every
                    // selectable country) stays implicit — POST [] so the
                    // backend treats it as all countries and OL-native-only
                    // countries aren't excluded from the public directory.
                    const narrowed = !allSelected && selectedCountries.length > 0;
                    const payload = narrowed ? selectedCountries : [];
                    await axios.post(`${API}/admin/general-settings`, {
                      enabled_countries: payload,
                    });
                    // Keep local state aligned with what was persisted.
                    if (payload.length === 0) setSelectedCountries([]);
                    setAllCountries(prev => prev.map(c => ({
                      ...c,
                      enabled: payload.length === 0 || payload.includes(c.code),
                    })));
                    setCountryDialog(false);
                    setCountryDirty(false);
                    toast.success(payload.length === 0
                      ? 'All countries enabled (worldwide)'
                      : `${payload.length} countr${payload.length === 1 ? 'y' : 'ies'} selected`);
                  } catch (err) {
                    console.error('Failed to save countries', err);
                    toast.error('Failed to save country selection');
                  }
                }}>
                  Save Selection
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default MapSettings;
